/**
 * Helpers for POA (Plan of Activities) rendering and parsing.
 *
 * The body of a POA item is plain text with optional @-mentions in
 * Markdown form: `@[Full Name](user_uuid)`. The frontend renders these
 * as first-name-only chips; the backend keeps the full form as the
 * source of truth and rebuilds the join table on every write.
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// Idempotent — date-utils.js also extends utc, but importing this file
// directly shouldn't depend on load order.
dayjs.extend(utc);

export const DAY_LABELS = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const DAY_LABELS_SHORT = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const DAY_LABELS_LETTER = ['', 'M', 'T', 'W', 'T', 'F', 'S', 'S'];

const UUID_PART = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
export const MENTION_RE = new RegExp(`@\\[([^\\]]+)\\]\\((${UUID_PART})\\)`, 'gi');

export type MentionToken = { type: 'mention'; fullName: string; userId: string };
export type TextToken = { type: 'text'; value: string };
export type BodyToken = MentionToken | TextToken;

/**
 * Tokenize a body string into alternating text + mention chunks.
 * Used by POAItem to render mentions as first-name chips.
 */
export function parseBody(body: string): BodyToken[] {
  if (!body) return [];
  const tokens: BodyToken[] = [];
  let last = 0;
  for (const m of body.matchAll(MENTION_RE)) {
    const start = m.index ?? 0;
    if (start > last) tokens.push({ type: 'text', value: body.slice(last, start) });
    tokens.push({ type: 'mention', fullName: m[1], userId: m[2] });
    last = start + m[0].length;
  }
  if (last < body.length) tokens.push({ type: 'text', value: body.slice(last) });
  return tokens;
}

/**
 * Display only the first name from a "First Last" full name string.
 * Falls back to the full string if there's no space.
 */
export function firstName(fullName: string): string {
  if (!fullName) return '';
  const idx = fullName.indexOf(' ');
  return idx === -1 ? fullName : fullName.slice(0, idx);
}

/**
 * Compute the Monday on/before a given date string (or "today" if omitted).
 * Operates in UTC so server↔client tz drift can't move the boundary.
 *
 * NOTE: dayjs `.day()` is *local-time* even on a `.utc()` instance unless
 * the call chains `.utc()` again at read time. We use the underlying Date
 * `.getUTCDay()` to be unambiguous — matches the backend's mondayOf() in
 * poa-router.js exactly.
 *
 * Returns YYYY-MM-DD.
 */
export function weekStartFor(dateStr?: string): string {
  const base = dateStr
    ? new Date(`${dateStr}T00:00:00Z`)
    : new Date();
  const dow = base.getUTCDay(); // 0=Sun..6=Sat
  const back = dow === 0 ? 6 : dow - 1;
  const monday = new Date(Date.UTC(
    base.getUTCFullYear(),
    base.getUTCMonth(),
    base.getUTCDate() - back,
  ));
  return monday.toISOString().slice(0, 10);
}

/**
 * Format the week range as "May 4 – May 10, 2026" or "Apr 28 – May 4, 2026".
 */
export function formatWeekRange(weekStart: string): string {
  const start = dayjs.utc(weekStart);
  const end = start.add(6, 'day');
  const sameMonth = start.month() === end.month();
  const sameYear = start.year() === end.year();
  if (sameMonth) return `${start.format('MMM D')}–${end.format('D, YYYY')}`;
  if (sameYear) return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
  return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
}

/**
 * For a given week_start (Monday) and day index 1..7, return the YYYY-MM-DD
 * date string. Used to map pilot-by-date keys onto day columns.
 */
export function dateForDay(weekStart: string, day: number): string {
  return dayjs.utc(weekStart).add(day - 1, 'day').format('YYYY-MM-DD');
}

/**
 * Header label for a day cell, e.g. "Mon · 5/4" — short month/day combo.
 */
export function dayHeaderLabel(weekStart: string, day: number): string {
  const d = dayjs.utc(weekStart).add(day - 1, 'day');
  return `${DAY_LABELS_SHORT[day]} · ${d.format('M/D')}`;
}

/**
 * Pretty-print a mention token's display form (first name only).
 */
export function mentionDisplayName(fullName: string): string {
  return firstName(fullName);
}
